/**
 * Heatmap Layer Configuration
 *
 * Provides heatmap visualization for soil moisture sensor data using
 * MapLibre GL JS heatmap layer specification.
 *
 * Features:
 * - Agricultural color scheme (brown → green → blue)
 * - Alternative themes (thermal, ocean, grayscale)
 * - Configurable radius, intensity, and opacity
 * - Zoom-based interpolation for smooth transitions
 *
 * @see https://maplibre.org/maplibre-style-spec/layers/#heatmap
 */

import type { HeatmapLayerSpecification } from 'maplibre-gl';
import type { HeatmapLayerConfig } from '../MapComponent.types';
import { MAP_COLOR_THEMES } from '../MapComponent.types';

// ============================================================================
// Heatmap Paint Configuration
// ============================================================================

/**
 * Generate heatmap paint configuration based on color theme and config
 *
 * @param colorTheme - Color theme name (agricultural, thermal, ocean, grayscale)
 * @param config - Heatmap layer configuration
 * @returns MapLibre heatmap paint specification
 */
export const getHeatmapPaint = (
  colorTheme: string = 'agricultural',
  config: HeatmapLayerConfig
): HeatmapLayerSpecification['paint'] => {
  const colors = MAP_COLOR_THEMES[colorTheme] || MAP_COLOR_THEMES.agricultural;

  // Debug log removed - runs on every render
  // console.log(`[HeatmapLayer] Generating paint config: theme=${colorTheme}, radius=${config.radius}, intensity=${config.intensity}`);

  return {
    // Weight based on moisture level (0-100)
    'heatmap-weight': [
      'interpolate',
      ['linear'],
      ['get', 'moisture'],
      0, 0,
      50, 0.5,
      100, 1,
    ],
    // Intensity increases with zoom
    'heatmap-intensity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      0, 1,
      9, config.intensity * 3,
    ],
    // Color gradient based on density
    'heatmap-color': [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0, colors[0],
      0.2, colors[1],
      0.4, colors[2],
      0.5, colors[3],
      0.6, colors[4],
      0.8, colors[5],
      1, colors[6] || colors[5],
    ],
    // Radius increases with zoom
    'heatmap-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      0, 2,
      9, config.radius,
    ],
    // Opacity setting with zoom-based fade for marker transition
    'heatmap-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      0, config.opacity,
      11, config.opacity,
      12, config.opacity * 0.5,
      13, 0,
    ],
  } as HeatmapLayerSpecification['paint'];
};

/**
 * Default heatmap layer configuration
 */
export const defaultHeatmapConfig: HeatmapLayerConfig = {
  enabled: true,
  colorScheme: 'agricultural',
  radius: 20,
  intensity: 1,
  opacity: 0.8,
};

/**
 * Heatmap layer ID constant
 */
export const HEATMAP_LAYER_ID = 'sensor-heatmap';

/**
 * Get full heatmap layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @param colorTheme - Color theme name
 * @param config - Heatmap layer configuration
 * @returns Full layer specification for MapLibre
 */
export const getHeatmapLayerSpec = (
  sourceId: string,
  colorTheme: string,
  config: HeatmapLayerConfig
): Omit<HeatmapLayerSpecification, 'source-layer'> => ({
  id: HEATMAP_LAYER_ID,
  type: 'heatmap',
  source: sourceId,
  paint: getHeatmapPaint(colorTheme, config),
});

export default {
  getHeatmapPaint,
  getHeatmapLayerSpec,
  defaultHeatmapConfig,
  HEATMAP_LAYER_ID,
};
